import * as React from 'react';
import { devUseWarning } from '../../../_util/warning';
import { getColumnKey, getColumnPos, renderColumnTitle } from '../../util';
import FilterDropdown, { flattenKeys } from './FilterDropdown';
const collectFilterStates = (columns, init, pos) => {
    let filterStates = [];
    (columns || []).forEach((column, index) => {
        const columnPos = getColumnPos(index, pos);
        const filterDropdownIsDefined = column.filterDropdown !== undefined;
        if (column.filters || filterDropdownIsDefined || 'onFilter' in column) {
            if ('filteredValue' in column) {
                // Controlled
                let filteredValues = column.filteredValue;
                if (!filterDropdownIsDefined) {
                    filteredValues = filteredValues?.map(String) ?? filteredValues;
                }
                filterStates.push({
                    column,
                    key: getColumnKey(column, columnPos),
                    filteredKeys: filteredValues,
                    forceFiltered: column.filtered,
                });
            }
            else {
                // Uncontrolled
                filterStates.push({
                    column,
                    key: getColumnKey(column, columnPos),
                    filteredKeys: (init && column.defaultFilteredValue
                        ? column.defaultFilteredValue
                        : undefined),
                    forceFiltered: column.filtered,
                });
            }
        }
        if ('children' in column) {
            filterStates = [...filterStates, ...collectFilterStates(column.children, init, columnPos)];
        }
    });
    return filterStates;
};
function injectFilter(prefixCls, dropdownPrefixCls, columns, filterStates, locale, triggerFilter, getPopupContainer, pos, rootClassName) {
    return columns.map((column, index) => {
        const columnPos = getColumnPos(index, pos);
        const { filterOnClose = true, filterMultiple = true, filterMode, filterSearch, } = column;
        let newColumn = column;
        if (newColumn.filters || newColumn.filterDropdown) {
            const columnKey = getColumnKey(newColumn, columnPos);
            const filterState = filterStates.find(({ key }) => columnKey === key);
            newColumn = {
                ...newColumn,
                title: (renderProps) => (React.createElement(FilterDropdown, { tablePrefixCls: prefixCls, prefixCls: `${prefixCls}-filter`, dropdownPrefixCls: dropdownPrefixCls, column: newColumn, columnKey: columnKey, filterState: filterState, filterOnClose: filterOnClose, filterMultiple: filterMultiple, filterMode: filterMode, filterSearch: filterSearch, triggerFilter: triggerFilter, locale: locale, getPopupContainer: getPopupContainer, rootClassName: rootClassName }, renderColumnTitle(column.title, renderProps))),
            };
        }
        if ('children' in newColumn) {
            newColumn = {
                ...newColumn,
                children: injectFilter(prefixCls, dropdownPrefixCls, newColumn.children, filterStates, locale, triggerFilter, getPopupContainer, columnPos, rootClassName),
            };
        }
        return newColumn;
    });
}
const generateFilterInfo = (filterStates) => {
    const currentFilters = {};
    filterStates.forEach(({ key, filteredKeys, column }) => {
        const keyAsString = key;
        const { filters, filterDropdown } = column;
        if (filterDropdown) {
            currentFilters[keyAsString] = filteredKeys || null;
        }
        else if (Array.isArray(filteredKeys)) {
            const keys = flattenKeys(filters);
            currentFilters[keyAsString] = keys.filter((originKey) => filteredKeys.includes(String(originKey)));
        }
        else {
            currentFilters[keyAsString] = null;
        }
    });
    return currentFilters;
};
export const getFilterData = (data, filterStates, childrenColumnName) => {
    const filterDatas = filterStates.reduce((currentData, filterState) => {
        const { column: { onFilter, filters }, filteredKeys, } = filterState;
        if (onFilter && filteredKeys && filteredKeys.length) {
            // Preprocess the keys corresponding to the filter tree,
            // use Map to improve lookup performance to O(1).
            const flatKeys = flattenKeys(filters);
            const keyMap = new Map();
            flatKeys.forEach((k) => {
                const strKey = String(k);
                if (!keyMap.has(strKey)) {
                    keyMap.set(strKey, k);
                }
            });
            const realKeys = filteredKeys.map((key) => {
                const strKey = String(key);
                return keyMap.get(strKey) ?? key;
            });
            const internalFilter = (subset) => subset.reduce((acc, record) => {
                const clonedRecord = { ...record };
                if (clonedRecord[childrenColumnName]) {
                    clonedRecord[childrenColumnName] = getFilterData(clonedRecord[childrenColumnName], filterStates, childrenColumnName);
                }
                if (realKeys.some((realKey) => onFilter(realKey, clonedRecord))) {
                    acc.push(clonedRecord);
                }
                return acc;
            }, []);
            return internalFilter(currentData);
        }
        return currentData;
    }, data);
    return filterDatas;
};
const getMergedColumns = (rawMergedColumns) => rawMergedColumns.flatMap((column) => {
    if ('children' in column) {
        return [column, ...getMergedColumns(column.children || [])];
    }
    return [column];
});
const useFilter = (props) => {
    const { prefixCls, dropdownPrefixCls, mergedColumns: rawMergedColumns, baseColumns, onFilterChange, getPopupContainer, locale: tableLocale, rootClassName, } = props;
    const warning = devUseWarning('Table');
    const mergedColumns = React.useMemo(() => getMergedColumns(baseColumns ?? rawMergedColumns ?? []), [baseColumns, rawMergedColumns]);
    const [filterStates, setFilterStates] = React.useState(() => collectFilterStates(mergedColumns, true));
    const mergedFilterStates = React.useMemo(() => {
        const collectedStates = collectFilterStates(mergedColumns, false);
        if (collectedStates.length === 0) {
            return collectedStates;
        }
        let filteredKeysIsAllNotControlled = true;
        let filteredKeysIsAllControlled = true;
        collectedStates.forEach(({ filteredKeys }) => {
            if (filteredKeys !== undefined) {
                filteredKeysIsAllNotControlled = false;
            }
            else {
                filteredKeysIsAllControlled = false;
            }
        });
        // Return if not controlled
        if (filteredKeysIsAllNotControlled) {
            // Filter column may have been removed
            const keyList = (mergedColumns || []).map((column, index) => getColumnKey(column, getColumnPos(index)));
            return filterStates.reduce((list, item) => {
                const keyIndex = keyList.indexOf(item.key);
                if (keyIndex !== -1) {
                    const col = mergedColumns[keyIndex];
                    list.push({
                        ...item,
                        column: {
                            ...item.column,
                            ...col,
                        },
                        forceFiltered: col.filtered,
                    });
                }
                return list;
            }, []);
        }
        warning(filteredKeysIsAllControlled, 'usage', 'Columns should all contain `filteredValue` or not contain `filteredValue`.');
        return collectedStates;
    }, [mergedColumns, filterStates]);
    const filters = React.useMemo(() => generateFilterInfo(mergedFilterStates), [mergedFilterStates]);
    const triggerFilter = (filterState) => {
        const newFilterStates = mergedFilterStates.filter(({ key }) => key !== filterState.key);
        newFilterStates.push(filterState);
        setFilterStates(newFilterStates);
        onFilterChange(generateFilterInfo(newFilterStates), newFilterStates);
    };
    const transformColumns = (innerColumns) => injectFilter(prefixCls, dropdownPrefixCls, innerColumns, mergedFilterStates, tableLocale, triggerFilter, getPopupContainer, undefined, rootClassName);
    return [transformColumns, mergedFilterStates, filters];
};
export { flattenKeys };
export default useFilter;
